/**
 * Phase B — network status + offline-queue flush hook.
 *
 * navigator.onLine alone is unreliable (it only reflects the local network
 * interface, not real backend reachability). On the `online` event we confirm
 * with a lightweight GET /api/ping before flushing the write queue.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { apiGet } from '../lib/api';
import { onQueueChange, queueCount, syncQueue } from '../lib/offlineQueue';

export function useNetworkStatus() {
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [justSynced, setJustSynced] = useState(false);
  const syncedTimer = useRef(null);

  const refreshCount = useCallback(async () => {
    try { setPending(await queueCount()); } catch { /* idb unavailable */ }
  }, []);

  const flush = useCallback(async () => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) return { synced: 0, failed: 0 };
    setSyncing(true);
    const res = await syncQueue();
    setSyncing(false);
    await refreshCount();
    if (res.synced > 0) {
      setJustSynced(true);
      if (syncedTimer.current) clearTimeout(syncedTimer.current);
      syncedTimer.current = setTimeout(() => setJustSynced(false), 4000);
    }
    return res;
  }, [refreshCount]);

  useEffect(() => {
    refreshCount();

    const onOnline = async () => {
      setOnline(true);
      try { await apiGet('/ping'); await flush(); } catch { /* still unreachable */ }
    };
    const onOffline = () => setOnline(false);

    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    const offQueue = onQueueChange(refreshCount);

    // Attempt a flush on mount if we're already online (covers the case where
    // the app re-opens with pending items and connectivity is already back).
    if (typeof navigator === 'undefined' || navigator.onLine) flush();

    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      offQueue();
      if (syncedTimer.current) clearTimeout(syncedTimer.current);
    };
  }, [flush, refreshCount]);

  return { online, pending, syncing, justSynced, flush, refreshCount };
}
